import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  MessageSquare,
  LifeBuoy,
} from 'lucide-react';

const Contact = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in your name, email, and message.');
      return;
    }
    setSubmitting(true);
    // No public contact API — deliver via the user's mail client
    const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`);
    const subject = encodeURIComponent(form.subject || 'Contact Form Inquiry');
    window.location.href = `mailto:info@optifyllc.com?subject=${subject}&body=${body}`;
    setTimeout(() => {
      setSubmitting(false);
      toast.success('Opening your email client to send the message.');
      setForm({ name: '', email: '', subject: '', message: '' });
    }, 600);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      lines: ['info@optifyllc.com'],
      note: 'We reply within one business day.',
    },
    {
      icon: Clock,
      title: 'Support Hours',
      lines: ['Monday – Friday', '9:00 AM – 6:00 PM'],
      note: 'Urgent incidents are monitored 24/7.',
    },
    {
      icon: MapPin,
      title: 'Office',
      lines: ['Optify LLC'],
      note: 'Serving customers worldwide.',
    },
  ];

  return (
    <div className="py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900">
            Contact <span className="text-primary-600">Us</span>
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Questions about the platform, pricing, or a partnership? Send us a message and
            we'll get back to you shortly.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            {contactInfo.map((info) => {
              const Icon = info.icon;
              return (
                <div
                  key={info.title}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
                >
                  <div className="flex items-start">
                    <div className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="ml-4">
                      <h3 className="font-semibold text-gray-900">{info.title}</h3>
                      {info.lines.map((line) => (
                        <p key={line} className="text-gray-700 text-sm mt-1">
                          {line}
                        </p>
                      ))}
                      <p className="text-gray-500 text-xs mt-2">{info.note}</p>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Existing users */}
            <div className="bg-primary-50 border border-primary-200 rounded-2xl p-6">
              <div className="flex items-start">
                <LifeBuoy className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                <div className="ml-4">
                  <h3 className="font-semibold text-primary-900">Already a user?</h3>
                  <p className="text-primary-800 text-sm mt-1">
                    The fastest way to get help is to{' '}
                    <Link to="/login" className="underline font-medium">
                      log in
                    </Link>{' '}
                    and open a support ticket — it goes straight to our team's queue.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            <div className="flex items-center mb-6">
              <MessageSquare className="h-6 w-6 text-primary-600 mr-3" />
              <h2 className="text-xl font-bold text-gray-900">Send us a message</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="How can we help?"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us a bit about your question or issue..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white px-6 py-3 rounded-lg font-medium transition-all hover:shadow-lg"
              >
                <Send className="h-4 w-4 mr-2" />
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
